import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

import type { ColumnDef } from './types';

export type ColumnChooserProps<Row> = {
  /** All columns in their current order, including hidden ones. */
  columns: ColumnDef<Row>[];
  hiddenKeys: Set<string>;
  onToggle: (key: string, visible: boolean) => void;
  onReset: () => void;
};

function labelFor<Row>(col: ColumnDef<Row>) {
  if (col.chooserLabel) return col.chooserLabel;
  return typeof col.header === 'string' && col.header.trim()
    ? col.header
    : col.key;
}

/**
 * Show/hide control for a `DataTable`.
 *
 * Columns marked `hideable: false` are omitted entirely rather than shown
 * disabled — a checkbox that can never be unticked is just noise.
 */
export function ColumnChooser<Row>({
  columns,
  hiddenKeys,
  onToggle,
  onReset,
}: ColumnChooserProps<Row>) {
  const hideable = columns.filter((col) => col.hideable !== false);
  if (!hideable.length) return null;

  const visibleCount = hideable.filter((c) => !hiddenKeys.has(c.key)).length;

  return (
    <Popover>
      <PopoverTrigger
        render={
          <Button variant="ghost" size="xs" title="Show or hide columns">
            <i className="pi pi-table text-xs" />
            Columns ({visibleCount}/{hideable.length})
          </Button>
        }
      />
      <PopoverContent align="start" className="w-56 p-0">
        <div className="max-h-72 overflow-y-auto p-1">
          {hideable.map((col) => {
            const visible = !hiddenKeys.has(col.key);
            return (
              <label
                key={col.key}
                className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-xs hover:bg-gray-100"
              >
                <Checkbox
                  aria-label={labelFor(col)}
                  checked={visible}
                  onCheckedChange={(checked) =>
                    onToggle(col.key, checked === true)
                  }
                />
                <span className="truncate">{labelFor(col)}</span>
              </label>
            );
          })}
        </div>
        <div className="border-t p-1">
          <Button
            variant="ghost"
            size="xs"
            className="w-full justify-center"
            onClick={onReset}
          >
            Reset columns
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
